import React, { useState, useEffect } from "react";
import { 
  RotateCw, 
  X, 
  Trash2, 
  Check, 
  XCircle, 
  RefreshCw, 
  Eye, 
  Link,
  FileText,
  MoreVertical,
  Download
} from "lucide-react";
import { 
  getKnowledgeBase, 
  deleteFile, 
  generateFileDownloadUrlById, 
  getFilePath 
} from "../../../../service/apiService";
import { toast } from "sonner";
import FilePreviewModal from "./FilePreviewModal";
import DropzoneLinks from "./DropzoneLinks";
import { useUser } from "../../../../context/UserContext";

// Define BrainBank type
interface BrainBank {
  id: string;
  knowledgebase_name: string;
  index_name: string;
  description: string;
  file_ids: string[];
  last_sync: string;
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
  summary: string | null;
  // Admin lock, when the API returns it; see app/utils/resource_lock.py.
  locked_by?: string | null;
  files?: Array<{
    id: string;
    file_name: string;
    original_file_name?: string;
    status: string;
    created_by: string;
    created_at: string;
    // Set on files that came in through a BrainBank upload link.
    dropzone_label?: string | null;
    dropzone_hold?: boolean | null;
  }>;
}

interface ViewBrainBankFilesProps {
  brainBank: BrainBank | null;
  onRefresh: () => void;
}

const ViewBrainBankFiles: React.FC<ViewBrainBankFilesProps> = ({ brainBank, onRefresh }) => {
  const { userDetails } = useUser();
  const [fileDetails, setFileDetails] = useState<BrainBank | null>(null);
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const [previewFile, setPreviewFile] = useState<{ url: string; name: string } | null>(null);
  const [filePaths, setFilePaths] = useState<{[fileId: string]: string}>({});
  const [actionMenuOpen, setActionMenuOpen] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [downloadingFiles, setDownloadingFiles] = useState<Set<string>>(new Set());

  // Check user permissions
  const canDownload = userDetails?.permissions?.files?.canDownload !== false;
  const canPreview = userDetails?.permissions?.files?.canPreview !== false;

  // Check if mobile view
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (brainBank) {
      handleGetBrainBankFiles();
    }
  }, [brainBank]);

  // Close action menus when clicking outside
  useEffect(() => {
    const handleOutsideClick = () => {
      setActionMenuOpen(null);
    };
    
    document.addEventListener('click', handleOutsideClick);
    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, []);

  const handleGetBrainBankFiles = async () => {
    if (!brainBank) return;
    
    setIsLoadingFiles(true);
    try {
      const response = await getKnowledgeBase(brainBank.id);
      setFileDetails(response);
    } catch (error) {
      console.error("Error loading files:", error);
      toast.error("Failed to load files");
    } finally {
      setIsLoadingFiles(false);
    }
  };

  const handlePreviewFile = async (fileId: string, fileName: string) => {
    if (!canPreview) {
      toast.error("You don't have permission to preview files");
      return;
    }
    
    try {
      toast.info("Generating preview...");
      const response = await generateFileDownloadUrlById(fileId);
      if (response && response.presigned_url) {
        setPreviewFile({ 
          url: response.presigned_url,
          name: fileName
        });
      } else {
        toast.error("Failed to generate preview URL");
      }
    } catch (error) {
      console.error("Error generating preview URL:", error);
      toast.error("Failed to preview file");
    }
  };
  
  const handleShowFilePath = async (fileId: string) => {
    try {
      toast.info("Retrieving file path...");
      const response = await getFilePath(fileId);
      if (response && response.file_path) {
        setFilePaths(prev => ({
          ...prev,
          [fileId]: response.file_path
        }));
        toast.success("File path retrieved");
      } else {
        toast.error("Failed to retrieve file path");
      }
    } catch (error) {
      console.error("Error retrieving file path:", error);
      toast.error("Failed to retrieve file path");
    }
  };
  
  const handleCloseFilePath = (fileId: string) => {
    setFilePaths(prev => {
      const newPaths = {...prev};
      delete newPaths[fileId];
      return newPaths;
    });
  };

  const handleDownloadFile = async (fileId: string, fileName: string) => {
    if (!canDownload) {
      toast.error("You don't have permission to download files");
      return;
    }
    
    try {
      setDownloadingFiles(prev => new Set(prev).add(fileId));
      toast.info("Preparing download...");
      
      const response = await generateFileDownloadUrlById(fileId);
      if (response && response.presigned_url) {
        // Fetch the file as blob and download it properly
        const fileResponse = await fetch(response.presigned_url);
        if (!fileResponse.ok) throw new Error('Download failed');
        
        const blob = await fileResponse.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        toast.success("File downloaded successfully");
      } else {
        toast.error("Failed to generate download URL");
      }
    } catch (error) {
      console.error("Error downloading file:", error);
      toast.error("Failed to download file");
      
      // Fallback: try to open in new tab
      try {
        const response = await generateFileDownloadUrlById(fileId);
        if (response && response.presigned_url) {
          window.open(response.presigned_url, '_blank');
          toast.info("File opened in new tab");
        }
      } catch (fallbackError) {
        console.error("Fallback also failed:", fallbackError);
      }
    } finally {
      setDownloadingFiles(prev => {
        const newSet = new Set(prev);
        newSet.delete(fileId);
        return newSet;
      });
    }
  };
  const handleDeleteFile = async (fileId: string) => {
    try {
      await deleteFile(fileId);
      toast.success("File deleted successfully");
      
      // Update local state
      if (fileDetails) {
        setFileDetails({
          ...fileDetails,
          files: fileDetails.files?.filter(f => f.id !== fileId) || []
        });
      }
      
      // Notify parent to refresh
      onRefresh();
    } catch (error) {
      toast.error("Failed to delete file");
      console.error("Error deleting file:", error);
    }
  };

  const toggleActionMenu = (fileId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setActionMenuOpen(actionMenuOpen === fileId ? null : fileId);
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  if (!brainBank) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">No BrainBank selected</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-3">
      {/* BrainBank Info */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h3 className="text-lg font-medium text-gray-900 mb-2">{brainBank.knowledgebase_name}</h3>
        <p className="text-sm text-gray-600 mb-3">{brainBank.description}</p>
        
        {isMobile ? (
          // Mobile: Stack vertically
          <div className="space-y-2 text-sm text-gray-500">
            <div>
              <span className="font-medium">Created:</span> {formatDate(brainBank.created_at)}
            </div>
            <div>
              <span className="font-medium">Last Sync:</span> {formatDate(brainBank.last_sync)}
            </div>
          </div>
        ) : (
          // Desktop: Side by side
          <div className="grid grid-cols-2 gap-4 text-sm text-gray-500">
            <div>
              <span className="font-medium">Created:</span> {formatDate(brainBank.created_at)}
            </div>
            <div>
              <span className="font-medium">Last Sync:</span> {formatDate(brainBank.last_sync)}
            </div>
          </div>
        )}
      </div>

      {/* Upload links: created and managed here, in place (plan section 15). */}
      <DropzoneLinks
        brainBank={{
          id: brainBank.id,
          knowledgebase_name: brainBank.knowledgebase_name,
          created_by: brainBank.created_by,
          locked_by: brainBank.locked_by,
        }}
        onChanged={handleGetBrainBankFiles}
      />

      {/* Header with Refresh Button */}
      <div className="flex justify-between items-center">
        <h4 className="text-md font-medium text-gray-900">Files</h4>
        <button
          onClick={handleGetBrainBankFiles}
          className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500"
          disabled={isLoadingFiles}
        >
          {isLoadingFiles ? (
            <RotateCw className="animate-spin h-4 w-4 mr-2" />
          ) : (
            <RefreshCw className="h-4 w-4 mr-2" />
          )}
          Refresh
        </button>
      </div>

      {/* Files List */}
      {isLoadingFiles ? (
        <div className="flex justify-center py-8">
          <RotateCw className="animate-spin h-8 w-8 text-violet-500" />
        </div>
      ) : !fileDetails?.files || fileDetails.files.length === 0 ? (
        <div className="text-center py-8">
          <FileText className="mx-auto h-12 w-12 text-gray-400" />
          <p className="mt-2 text-sm text-gray-500">No files found in this BrainBank</p>
        </div>
      ) : (
        <div className="space-y-3">
          {fileDetails.files.map((file) => (
            <div key={file.id} className="bg-white rounded-lg shadow-sm border border-gray-100 p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3 min-w-0 flex-1">
                  {/* Status Icon */}
                  {file.status.toLowerCase() === 'processed' ? (
                    <Check size={18} className="text-green-500 flex-shrink-0" />
                  ) : file.status.toLowerCase() === 'failed' ? (
                    <XCircle size={18} className="text-red-500 flex-shrink-0" />
                  ) : (
                    <RotateCw size={18} className="text-blue-500 animate-spin flex-shrink-0" />
                  )}
                  
                  {/* File Info */}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {file.original_file_name || file.file_name}
                    </p>
                    <p className="text-xs text-gray-500">
                      Status: {file.dropzone_hold ? "awaiting approval" : file.status}
                      {file.dropzone_label ? ` · via upload link: ${file.dropzone_label}` : ""}
                    </p>
                    <p className="text-xs text-gray-400">
                      Created: {formatDate(file.created_at)}
                    </p>
                  </div>
                </div>
                
                {/* Actions */}
                {isMobile ? (
                  // Mobile: Action menu
                  <div className="flex items-center" onClick={(e) => e.stopPropagation()}>
                    <div className="relative">
                      <button 
                        onClick={(e) => toggleActionMenu(file.id, e)}
                        className="p-1 rounded-full focus:outline-none text-gray-500"
                        aria-label="More options"
                      >
                        <MoreVertical className="h-5 w-5" />
                      </button>
                        
                      {actionMenuOpen === file.id && (
                        <div 
                          className="absolute right-0 mt-1 bg-white shadow-lg rounded-md border border-gray-200 z-10 p-1"
                          onClick={(e) => e.stopPropagation()}
                          style={{ minWidth: '140px' }}
                        >
                          <div className="flex items-center justify-around space-x-1">
                            <button 
                              className={`p-2 rounded ${
                                canPreview 
                                  ? "text-violet-600 hover:bg-gray-100" 
                                  : "text-gray-400 cursor-not-allowed"
                              }`}
                              onClick={() => handlePreviewFile(file.id, file.original_file_name || file.file_name)}
                              aria-label="Preview"
                              title={canPreview ? "Preview" : "Preview disabled - no permission"}
                              disabled={!canPreview}
                            >
                              <Eye className="h-4 w-4" />
                            </button>

                            <button 
                              className={`p-2 rounded ${
                                canDownload && !downloadingFiles.has(file.id)
                                  ? "text-green-600 hover:bg-gray-100" 
                                  : "text-gray-400 cursor-not-allowed"
                              }`}
                              onClick={() => handleDownloadFile(file.id, file.original_file_name || file.file_name)}
                              aria-label="Download"
                              title={canDownload ? "Download" : "Download disabled - no permission"}
                              disabled={!canDownload || downloadingFiles.has(file.id)}
                            >
                              {downloadingFiles.has(file.id) ? (
                                <RotateCw className="h-4 w-4 animate-spin" />
                              ) : (
                                <Download className="h-4 w-4" />
                              )}
                            </button>
                            
                            <button 
                              className="p-2 rounded text-blue-600 hover:bg-gray-100"
                              onClick={() => handleShowFilePath(file.id)}
                              aria-label="File Path"
                              title="File Path"
                            >
                              <Link className="h-4 w-4" />
                            </button>
                            
                            <button 
                              className="p-2 rounded text-red-600 hover:bg-gray-100"
                              onClick={() => handleDeleteFile(file.id)}
                              aria-label="Delete"
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  // Desktop: Horizontal buttons
                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={() => handlePreviewFile(file.id, file.original_file_name || file.file_name)}
                      className={`p-2 rounded-full ${
                        canPreview
                          ? "text-violet-600 hover:text-violet-800 hover:bg-violet-50"
                          : "text-gray-400 cursor-not-allowed"
                      }`}
                      title={canPreview ? "Preview file" : "Preview disabled - no permission"}
                      disabled={!canPreview}
                    >
                      <Eye size={16} />
                    </button>

                    <button
                      onClick={() => handleDownloadFile(file.id, file.original_file_name || file.file_name)}
                      className={`p-2 rounded-full ${
                        canDownload && !downloadingFiles.has(file.id)
                          ? "text-green-600 hover:text-green-800 hover:bg-green-50"
                          : "text-gray-400 cursor-not-allowed"
                      }`}
                      title={canDownload ? "Download file" : "Download disabled - no permission"}
                      disabled={!canDownload || downloadingFiles.has(file.id)}
                    >
                      {downloadingFiles.has(file.id) ? (
                        <RotateCw size={16} className="animate-spin" />
                      ) : (
                        <Download size={16} />
                      )}
                    </button>
                    
                    <button
                      onClick={() => handleShowFilePath(file.id)}
                      className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-full"
                      title="Show file path"
                    >
                      <Link size={16} />
                    </button>
                    
                    <button
                      onClick={() => handleDeleteFile(file.id)}
                      className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-full"
                      title="Delete file"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                )}
              </div>
              
              {/* File Path Display */}
              {filePaths[file.id] && (
                <div className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                  <div className="flex justify-between items-center">
                    <h4 className="text-sm font-medium text-gray-700">File Path:</h4>
                    <button 
                      onClick={() => handleCloseFilePath(file.id)} 
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X size={16} />
                    </button>
                  </div>
                  <div className="mt-2 text-xs font-mono bg-white p-2 border border-gray-200 rounded overflow-x-auto">
                    {filePaths[file.id]}
                  </div>
                  <div className="mt-2 flex justify-end">
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(filePaths[file.id]);
                        toast.success("Path copied to clipboard");
                      }}
                      className="text-xs text-violet-600 hover:text-violet-800"
                    >
                      Copy to clipboard
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      
      {/* File Preview Modal */}
      {previewFile && (
        <FilePreviewModal
          fileUrl={previewFile.url}
          fileName={previewFile.name}
          onClose={() => setPreviewFile(null)}
        />
      )}
    </div>
  );
};

export default ViewBrainBankFiles;